import * as React from 'react';

import { AngularContext } from '../../ts/factories/AngularContextFactory';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { RefreshPropertyHelper } from '../../ts/helpers/RefreshPropertyHelper';
import { IContentInfo } from '../../ts/models/IContentInfo';
import { IDataSourceDetailInfo } from '../../ts/models/IDataSourceInfo';
import { IRefreshExtractAction } from '../../ts/ServerApi/types/actionNames';
import { IDatasourceActions } from '../../ts/ServerApi/types/IDatasourceActions';
import { DisplayMode } from '../../ts/ServerApi/types/ISessionInfo';
import { CreateExtractTasksMode } from '../../ts/services/CreateExtractTasksAction';
import { Telemetry } from '../../ts/services/TelemetryService';
import { createExtractRefresh } from '../actions/spaces/CreateExtractRefreshAction';
import { createRemoteExtractRefresh } from '../actions/spaces/CreateRemoteExtractRefreshAction';
import { setRemoteExtractRefreshProperties } from '../actions/spaces/SetRemoteExtractRefreshPropertiesAction';
import { appStore } from '../AppStore';
import { SpaceType, SubspaceName } from '../state/SpaceInfo';
import { currentUserIsOwnerOrAdmin, hasExtractRefreshPermission } from '../utils/MixedContentHelpers';
import { ExplorerEmptyStateProps } from './Components/ExplorerEmpyState';
import { ConnectedExplorer } from './Explorer/ConnectedExplorer';
import { ConnectedCreateContentButton } from './Explorer/CreateContent/ConnectedCreateContentButton';
import { NewExtractRefreshButton } from './Explorer/CreateExtractRefreshButton';
import { ExplorerToolbarVisibility } from './Explorer/Explorer';
import { ContentSpecificControls } from './Explorer/ExplorerToolbar';
import { RemoteRefreshPropertiesButton } from './Explorer/RemoteRefreshPropertiesButton';
import { Subspace } from './TabbedSpace';

export interface SubspaceParams {
  name: SubspaceName;
  tabLabel: string;
  supportedDisplayModes: ReadonlyArray<DisplayMode>; // TODO this should come from ExplorableItemTypeDefinition?
  angularContext: AngularContext;
  telemetry: Telemetry;
  shouldRenderContentCount: boolean;
  contentCount?: number;
  contentCountUpToDate?: boolean;
  toolbarContentFilterControl?: JSX.Element;
  toolbarContentSpecificControls?: ContentSpecificControls;
  supportFilterPanel?: boolean;
  hideSelection?: boolean;
  hideSelectionAndActionMenu?: boolean;
  toolbarVisibility: ExplorerToolbarVisibility;
  emptyStateProps?: ExplorerEmptyStateProps;
}

export function explorerSubspace(params: SubspaceParams): Subspace {
  return {
    name: params.name,
    tabLabel: params.tabLabel,
    tabContentCount: params.contentCount,
    shouldRenderTabContentCount: params.shouldRenderContentCount,
    tabContentCountUpToDate: params.contentCountUpToDate,
    renderContent: () =>
      <ConnectedExplorer
        angularContext={params.angularContext}
        supportedDisplayModes={params.supportedDisplayModes}
        telemetry={params.telemetry}
        hideSelectionAndActionMenu={ params.hideSelectionAndActionMenu}
        toolbarProps= {{
          supportFilterPanel: params.supportFilterPanel,
          hideSelection:params.hideSelection,
          toolbarContentSpecificControls: params.toolbarContentSpecificControls,
          toolbarVisibility: params.toolbarVisibility,
          toolbarContentFilterControl: params.toolbarContentFilterControl,
        }}
        emptyStateProps={params.emptyStateProps}
      />
  };
}

export function newExtractRefreshButton(
  content: IContentInfo,
  actions: IRefreshExtractAction,
): JSX.Element {
  const appState = appStore.getState();

  if (currentUserIsOwnerOrAdmin(appState, content) || actions.refreshExtract) {
    const action = createExtractRefresh([content],
        FeatureFlags.bridgeServerTriggeredClientSideRefresh ?
          CreateExtractTasksMode.AllowScheduleOnServerAndClient : CreateExtractTasksMode.AllowScheduleOnServer);
    return <NewExtractRefreshButton handleClick={() => appStore.dispatch(action)}/>;
  }

  return null;
}

export function newRemoteExtractRefreshButton(
  datasource: IDataSourceDetailInfo
): JSX.Element {
  const appState = appStore.getState();

  if (currentUserIsOwnerOrAdmin(appState, datasource)) {
    const action = createRemoteExtractRefresh(datasource);
    return <NewExtractRefreshButton handleClick={() => appStore.dispatch(action)}/>;
  }

  return null;
}

export function remoteExtractRefreshPropertiesButton(
  datasource: IDataSourceDetailInfo,
  datasourceActions: IDatasourceActions
): JSX.Element {
  const appState = appStore.getState();

  const refreshPropertiesAreEditable = () => {
    return hasExtractRefreshPermission(appState, datasource, datasourceActions)
      && RefreshPropertyHelper.canBeRefreshedRemotely(datasource);
  };

  const handleClick = () => appStore.dispatch(setRemoteExtractRefreshProperties(datasource));

  return <RemoteRefreshPropertiesButton datasource={datasource}
                                        refreshPropertiesAreEditable={refreshPropertiesAreEditable}
                                        handleClick={handleClick}/>;
}

export function createContentButton(projectId: string, spaceType: SpaceType, flyoutWidth?: number): JSX.Element {
  return <ConnectedCreateContentButton projectId={projectId} spaceType={spaceType} flyoutWidth={flyoutWidth}/>;
}
